import { memo, useState, useEffect } from 'react';
import { View, Text, FlatList, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useConversations } from '@/hooks/useConversations';
import { ConversationWithStudent } from '@/lib/types';
import { ConversationView } from './ConversationView';

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export function ChatDesktop() {
  const { profile } = useAuth();
  const { conversations = [], isLoading } = useConversations(profile?.id ?? '');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Seleciona automaticamente a primeira conversa (primeira com unread, depois mais recente)
  useEffect(() => {
    if (!selectedId && conversations.length > 0) {
      const firstUnread = conversations.find(c => c.unread_personal > 0);
      setSelectedId((firstUnread ?? conversations[0]).id);
    }
  }, [conversations, selectedId]);

  const selected = conversations.find(c => c.id === selectedId);

  return (
    <View style={styles.shell}>

      {/* Lista lateral */}
      <View style={styles.listPane}>
        <View style={styles.listHeader}>
          <Text style={styles.listTitle}>Conversas</Text>
          <Text style={styles.listCount}>{conversations.length}</Text>
        </View>

        {isLoading ? (
          <ActivityIndicator color="#C8102E" style={{ marginTop: 40 }} />
        ) : conversations.length === 0 ? (
          <View style={styles.empty}>
            <Text style={styles.emptyText}>Nenhuma conversa ainda.</Text>
            <Text style={styles.emptySub}>Quando um aluno abrir o chat, aparece aqui.</Text>
          </View>
        ) : (
          <FlatList
            data={conversations}
            keyExtractor={c => c.id}
            renderItem={({ item }) => (
              <ConversationListItem
                conv={item}
                active={item.id === selectedId}
                onPress={() => setSelectedId(item.id)}
              />
            )}
          />
        )}
      </View>

      {/* Viewer */}
      <View style={styles.viewerPane}>
        {selected ? (
          <>
            <View style={styles.viewerHeader}>
              <View style={styles.avatar}>
                <Text style={styles.avatarLetter}>
                  {(selected.student?.full_name ?? '?').charAt(0).toUpperCase()}
                </Text>
              </View>
              <Text style={styles.viewerName}>{selected.student?.full_name ?? 'Aluno'}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <ConversationView key={selected.id} conversationId={selected.id} />
            </View>
          </>
        ) : (
          <View style={styles.placeholder}>
            <Ionicons name="chatbubbles-outline" size={40} color="#ccc" />
            <Text style={styles.placeholderText}>Selecione uma conversa</Text>
          </View>
        )}
      </View>
    </View>
  );
}

/**
 * Item individual da lista de conversas no ChatDesktop.
 *
 * Memoizado pra evitar re-render de toda a lista quando 1 conversa
 * recebe nova mensagem (apenas a linha afetada re-renderiza).
 */
const ConversationListItem = memo(function ConversationListItem({
  conv, active, onPress,
}: { conv: ConversationWithStudent; active: boolean; onPress: () => void }) {
  const hasUnread = conv.unread_personal > 0;
  const name = conv.student?.full_name ?? 'Aluno';
  return (
    <TouchableOpacity
      style={[styles.item, active && styles.itemActive]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={styles.avatar}>
        <Text style={styles.avatarLetter}>{name.charAt(0).toUpperCase()}</Text>
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={[styles.itemName, hasUnread && styles.itemNameBold]} numberOfLines={1}>
          {name}
        </Text>
        <Text style={styles.itemTime}>
          {conv.last_message_at ? relativeTime(conv.last_message_at) : 'sem mensagens'}
        </Text>
      </View>
      {hasUnread && (
        <View style={styles.badge}>
          <Text style={styles.badgeText}>
            {conv.unread_personal > 9 ? '9+' : String(conv.unread_personal)}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  shell: { flex: 1, flexDirection: 'row', backgroundColor: '#F5F5F5' },

  listPane: {
    width: 320,
    backgroundColor: '#fff',
    borderRightWidth: 1,
    borderRightColor: '#EEE',
  },
  listHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 18, paddingTop: 22, paddingBottom: 14,
    borderBottomWidth: 1, borderBottomColor: '#F0F0F0',
  },
  listTitle: { fontSize: 16, fontWeight: '800', color: '#1A1A1A' },
  listCount: { fontSize: 11, color: '#888', fontWeight: '600' },

  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  emptyText: { fontSize: 14, color: '#888', fontWeight: '600' },
  emptySub: { fontSize: 12, color: '#aaa', marginTop: 6, textAlign: 'center' },

  item: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 16, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: '#F5F5F5',
  },
  itemActive: { backgroundColor: '#FFF0F2' },
  avatar: {
    width: 38, height: 38, borderRadius: 19,
    backgroundColor: '#C8102E22',
    alignItems: 'center', justifyContent: 'center',
  },
  avatarLetter: { fontSize: 14, fontWeight: '700', color: '#C8102E' },
  itemName: { fontSize: 13, color: '#1A1A1A' },
  itemNameBold: { fontWeight: '700' },
  itemTime: { fontSize: 11, color: '#888', marginTop: 2 },
  badge: {
    backgroundColor: '#C8102E', borderRadius: 10,
    minWidth: 20, height: 20, paddingHorizontal: 5,
    alignItems: 'center', justifyContent: 'center',
  },
  badgeText: { color: '#fff', fontSize: 10, fontWeight: '700' },

  viewerPane: { flex: 1, backgroundColor: '#F5F5F5' },
  viewerHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 18, paddingVertical: 14,
    backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#EEE',
  },
  viewerName: { fontSize: 15, fontWeight: '700', color: '#1A1A1A' },

  placeholder: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10 },
  placeholderText: { color: '#aaa', fontSize: 13 },
});
